import {
  AuthError,
  type AuthenticationResult,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  CacheLookupPolicy,
  ClientAuthError,
  ClientAuthErrorCodes,
  type Configuration,
  type EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionRequiredAuthErrorCodes,
  PublicClientApplication,
} from '@azure/msal-browser';
import { Errors } from '@azure/msal-common/browser';

import type { AuthType } from '../models/auth.model';
import { authStorage, isRedirectablePath } from './helpers';
import { clearTokenRefresh, registerTokenRefresh } from './registry';

/**
 * Refresh-token failures that MSAL would normally try to resolve with a hidden
 * iframe. Iframe renewal is disabled (see `refreshToken`), so these surface
 * directly, and they all mean the same thing: the cached refresh token can no
 * longer produce an access token, so the user has to authenticate again.
 */
const INTERACTIVE_LOGIN_ERROR_CODES: string[] = [
  // Refresh token rejected by the identity provider (revoked, expired or replaced).
  Errors.INVALID_GRANT_ERROR,
  ClientAuthErrorCodes.tokenRefreshRequired,
];

function buildRedirectPath(): string | null {
  const { pathname, search } = new URL(window.location.href);
  return isRedirectablePath(pathname) ? `${pathname}${search}` : null;
}

/**
 * True when a failed silent token acquisition can only be resolved by sending
 * the user through an interactive login.
 * @param error - Error thrown by `acquireTokenSilent`
 */
function requiresInteractiveLogin(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }
  return (
    error instanceof AuthError &&
    INTERACTIVE_LOGIN_ERROR_CODES.includes(error.errorCode)
  );
}

class MsalManager {
  private eventCallbackId: string | null = null;
  private clientId: string;
  private boundRefreshToken:
    | (() => Promise<AuthenticationResult | undefined>)
    | null = null;

  public readonly authType: AuthType;
  public readonly instance: PublicClientApplication;
  public readonly ready: Promise<void>;

  constructor(authType: AuthType, config: Configuration) {
    this.authType = authType;
    this.clientId = config.auth.clientId;
    this.instance = new PublicClientApplication(config);
    authStorage.auth_type.clearAuthType();
    authStorage.auth_type.setAuthType(authType);

    this.ready = this.init();
  }

  private async init() {
    try {
      await this.instance.initialize();
      this.boundRefreshToken = this.refreshToken.bind(this);
      registerTokenRefresh(this.boundRefreshToken);
      this.setupEventCallback();
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await this.forceReauthentication();
      }
      console.error('Failed to initialize MSAL manager:', error);
    }
  }

  public dispose(): void {
    if (this.eventCallbackId) {
      this.instance.removeEventCallback(this.eventCallbackId);
      this.eventCallbackId = null;
    }

    if (this.boundRefreshToken) {
      clearTokenRefresh(this.boundRefreshToken);
      this.boundRefreshToken = null;
    }
  }

  private setupEventCallback(): void {
    if (this.eventCallbackId) {
      this.instance.removeEventCallback(this.eventCallbackId);
    }

    this.eventCallbackId = this.instance.addEventCallback(
      async (event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS) {
          if (event.payload && 'account' in event.payload) {
            const { account } = event.payload;
            if (account) {
              this.instance.setActiveAccount(account);
            }
          }
        }
      }
    );
  }

  private async forceReauthentication(): Promise<void> {
    const postLogoutRedirectUri =
      this.instance.getConfiguration().auth.postLogoutRedirectUri;
    const url = new URL(postLogoutRedirectUri ?? '', window.location.origin);

    const redirectPath = buildRedirectPath();
    if (redirectPath) {
      url.searchParams.set('redirectPath', redirectPath);
    }

    try {
      await this.instance.logoutRedirect({
        account: this.instance.getActiveAccount(),
        postLogoutRedirectUri: url.toString(),
      });
    } catch (error) {
      const isInteractionInProgress =
        error instanceof BrowserAuthError &&
        error.errorCode === BrowserAuthErrorCodes.interactionInProgress;

      if (isInteractionInProgress) {
        return;
      }

      const isEndpointResolutionError =
        error instanceof ClientAuthError &&
        error.errorCode === ClientAuthErrorCodes.endpointResolutionError;

      if (isEndpointResolutionError) {
        console.error(
          'MSAL endpoint resolution failed during logout, performing local cleanup:',
          error
        );
        authStorage.auth_type.clearAuthType();
        try {
          await this.instance.clearCache();
        } catch (cacheError) {
          console.error(
            'Failed to clear MSAL cache during logout recovery:',
            cacheError
          );
        }
        window.location.assign(postLogoutRedirectUri || '/login');
        return;
      }
      throw error;
    }
  }

  public async refreshToken(): Promise<AuthenticationResult | undefined> {
    const account = this.instance.getActiveAccount();
    if (!account) {
      console.error(
        new InteractionRequiredAuthError(
          InteractionRequiredAuthErrorCodes.loginRequired,
          'No active account set, force authentication.'
        )
      );
      await this.forceReauthentication();
      return;
    }

    try {
      const response = await this.instance.acquireTokenSilent({
        scopes: [this.clientId],
        account,
        /*
         * Cache lookup and refresh-token redemption only. The `Default` policy
         * also falls back to a hidden iframe, which cannot complete here: the
         * apps are served with `frame-ancestors 'none'`, so the identity
         * provider's redirect back into the frame is blocked and MSAL only gives
         * up after `iframeHashTimeout`. We authenticate by redirect, so go
         * straight to interactive login instead of waiting on that.
         */
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      });
      return response;
    } catch (error) {
      if (requiresInteractiveLogin(error)) {
        await this.forceReauthentication();
        return;
      }

      // throw if all else fails
      console.error('Failed to refresh token:', error);
      throw error;
    }
  }
}

export default MsalManager;
